import { useRender } from "@base-ui/react/use-render"
import { type VariantProps, cva } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:border-destructive",
  {
    variants: {
      // ⚠️ Adding a variant? It is UNFLOORED (no 44px mobile tap target)
      // until you add it to `compoundVariants` below — OKS-874. Nothing will
      // tell you: this file is in eslint's `ignores`, so there is no lint, no
      // type error and no failing test. This comment is the whole guard.
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // OKS-874 — 44px mobile touch-target floor below `md` (768px).
      //
      // `max-md:min-h-11` (Tailwind `11` = 2.75rem = 44px) rather than a base
      // floor plus an `md:` reset, for three reasons:
      //   1. It cannot be half-applied. A base+reset pair is two tokens that
      //      must stay in sync; forget the reset and desktop silently grows.
      //   2. It makes "unchanged at ≥768px" an ABSENCE OF DECLARATION rather
      //      than a behaviour to verify — no rule exists up there at all.
      //   3. It matches the in-repo `max-sm:h-11` idiom.
      //
      // A FLOOR (`min-h-*`), never a fixed height (`h-*`): `min-height` beats
      // `height` regardless of specificity, so this composes with the ~124
      // call sites already carrying hand-written height patches in nine
      // dialects instead of fighting them. A site restating `h-9` still
      // floors correctly.
      //
      // Every `icon*` size floors in BOTH dimensions. A height-only floor on a
      // square yields 36×44 — not a 44px target, just a 36px target that got
      // taller. `size-*` and `min-h`/`min-w` are different CSS property
      // groups, so there is nothing for tailwind-merge to resolve.
      //
      // `sm` is deliberately NOT floored — deferred, not exempt. Its growth
      // risk is visual and concentrated on dense surfaces, which needs a 375px
      // manual review pass. See PRD §7.1. (`icon-sm` was re-ruled INTO scope,
      // PRD §7.11: 0 direct call sites + 1 wrapper, so there is no
      // unreviewable population to defer against.)
      //
      // ⚠️ The floor tokens live in `compoundVariants` BELOW, not here. The
      // exemption PRD §3 grants is keyed on `variant` (`link`), while the
      // floor itself is keyed on `size` — and cva's size slot applies to
      // every variant. Put the tokens on the size slot and `variant="link"`
      // receives them unconditionally: `min-height` beats `height` regardless
      // of specificity, so a prose link carrying `h-auto … p-0` becomes a 44px
      // slab below `md`. An exemption keyed on a different axis than its
      // mechanism cannot be honoured without a cross-axis construct.
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    // The cross-axis construct. `variant` is enumerated POSITIVELY (every
    // variant except `link`) rather than negated, because cva has no "not"
    // and a negating entry would have to emit a counter-token
    // (a max-md-scoped min-height of zero — the literal token is spelled out
    // nowhere in this repo ON PURPOSE: Tailwind scans comments, so writing it
    // here would EMIT that utility into the bundle. It would be dead CSS, and
    // worse, it would make "is this class in the built stylesheet?" useless as
    // forensic evidence — a check that returns the same answer whether or not
    // any element uses it) — which leaves a `max-md:min-*` declaration on the
    // link and only wins by tailwind-merge ordering. Listing the floored
    // variants means `variant="link"` emits no floor token at all, at any
    // size. A new variant added to the slot above is unfloored until it is
    // added here — the safe default for a rule whose failure mode is
    // inflating text.
    compoundVariants: [
      {
        variant: ["default", "destructive", "outline", "secondary", "ghost"],
        size: ["default", "lg"],
        class: "max-md:min-h-11",
      },
      {
        variant: ["default", "destructive", "outline", "secondary", "ghost"],
        size: ["icon", "icon-sm", "icon-lg"],
        class: "max-md:min-h-11 max-md:min-w-11",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  ref,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  // `asChild` is preserved from the Radix `Slot` API: when set, the single
  // child element is rendered in place (no wrapping element) via Base UI's
  // `useRender`, which forwards `ref`, joins `className`, and merges event
  // handlers onto that child.
  return useRender({
    ref,
    render: asChild ? (
      (children as React.ReactElement)
    ) : (
      <button>{children}</button>
    ),
    props: {
      "data-slot": "button",
      className: cn(buttonVariants({ variant, size, className })),
      ...props,
    },
  })
}

export { Button, buttonVariants }
